import _, {cloneDeep} from 'lodash';
import {Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader} from "@heroui/react";
import {useMutation, useQueryClient} from '@tanstack/react-query';
import repositories from '../repositories.jsx';
import toast from 'react-hot-toast';
import {useRecoilState} from 'recoil';
import {userState} from '../store.jsx';
import {useNavigate} from 'react-router-dom';
import {useEffect, useMemo, useState} from 'react';
import moment from 'moment';
import {animate, motion, useMotionValue, useTransform} from 'framer-motion';

/* Animated count-up for the plan price */
function CountUp({value = 0, className}) {
	const mv = useMotionValue(0);
	const rounded = useTransform(mv, (v) => Math.round(v).toLocaleString());
	useEffect(() => {
		const controls = animate(mv, value, {duration: 1, ease: [0.16, 1, 0.3, 1]});
		return controls.stop;
	}, [value, mv]);
	return <motion.span className={className}>{rounded}</motion.span>;
}

/* Per-tier accent palette — keeps the brand family but gives each card its own identity */
const ACCENTS = [
	{rail: 'from-violet-500 to-fuchsia-500', text: 'text-violet-600', soft: 'bg-violet-500/10', dot: 'bg-violet-500', glow: 'rgba(124,58,237,0.45)'},
	{rail: 'from-amber-400 to-orange-500', text: 'text-amber-600', soft: 'bg-amber-500/10', dot: 'bg-amber-500', glow: 'rgba(245,158,11,0.40)'},
	{rail: 'from-fuchsia-500 to-pink-500', text: 'text-fuchsia-600', soft: 'bg-fuchsia-500/10', dot: 'bg-fuchsia-500', glow: 'rgba(217,70,239,0.42)'},
	{rail: 'from-indigo-500 to-violet-500', text: 'text-indigo-600', soft: 'bg-indigo-500/10', dot: 'bg-indigo-500', glow: 'rgba(99,102,241,0.42)'},
];

export default function PlanCard({data, planKey, ...props}) {
	const queryClient = useQueryClient()
	const navigate = useNavigate()
	const [authMe, setAuthMe] = useRecoilState(userState)
	const [isButtonLoading, setIsButtonLoading] = useState(false)
	
	const subscribePlan = useMutation({
		mutationFn: repositories.subscription.createSubscription,
		onMutate: () => setIsButtonLoading(true),
		onSuccess: async (response) => {
			if (response.payment_link) {
				await window.open(response.payment_link, '_self')
			} else {
				toast.success(response?.message)
			}
			setTimeout(() => {
				queryClient.refetchQueries({
					queryKey: ['auth']
				})
			}, 1000)
			await setAuthMe(_.merge(cloneDeep(authMe), {
				current_plan: data?.monthly_price_id,
				current_plan_name: data?.name
			}))
		},
		onError: (error) => {
			toast.error(error?.response.data?.detail || 'Something went wrong, please try again')
		},
		onSettled: () => setIsButtonLoading(false)
	})
	
	const getCurrentPlanID = useMemo(() => {
		return authMe?.current_plan || 1
	}, [authMe.current_plan])
	
	const getButtonName = useMemo(() => {
		if (getCurrentPlanID === data?.monthly_price_id) {
			return 'Subscribed';
		} else if (_.includes([0, 9, 10, 11], getCurrentPlanID) && _.includes([1, 2], data?.monthly_price_id)) {
			return 'Subscribed';
		} else if ((authMe?.trial_status || 'initial') === 'initial') {
			if (_.includes([7, 5], data?.monthly_price_id)) {
				return 'Subscribe';
			} else {
				if (_.includes([1, 2], getCurrentPlanID)) {
					return <div>
						<div>Try for free</div>
						<span className="text-xs capitalize">No credit card required</span>
					</div>;
				}
			}
		} else if (!_.includes([9], getCurrentPlanID) && getCurrentPlanID > data?.monthly_price_id) {
			return 'Select';
		}
		return 'Upgrade';
	}, [getCurrentPlanID, data?.monthly_price_id, authMe?.trial_status]);

	/* True when this card represents the user's active plan (button reads "Subscribed") */
	const isCurrentPlan = _.snakeCase(getButtonName) === 'subscribed';

	/* Visual identity for this tier */
	const accent = ACCENTS[planKey % ACCENTS.length];
	const tierNo = String((planKey ?? 0) + 1).padStart(2, '0');

	const [confirmSubscription, setConfirmSubscription] = useState(false)
	
	const handleSubscribe = useMutation({
		mutationFn: repositories.subscription.checkSubscription,
		onMutate: () => setIsButtonLoading(true),
		onSuccess: data => {
			setConfirmSubscription(true)
		},
		onError: (error) => {
			toast.error(error?.response.data?.detail || 'Something went wrong, please try again')
		},
		onSettled: () => setIsButtonLoading(false)
	})
	
	const onHandelSubscribe = async () => {
		if ((authMe?.trial_status || 'initial') === 'initial' && !_.includes([7, 1, 5], data?.monthly_price_id)) {
			subscribePlan.mutate({price_id: data?.trial_id})
		} else {
			if (_.includes([3, 5, 7], data?.monthly_price_id)) {
				handleSubscribe.mutate({price_id: data?.monthly_price_id})
			} else {
				subscribePlan.mutate({price_id: data?.monthly_price_id})
			}
		}
	}
	
	/* Plan-change modal helpers */
	const isDowngrade = !_.includes([9], getCurrentPlanID) && getCurrentPlanID > data?.monthly_price_id;
	const newPriceInt = Number(_.get(_.split(data?.monthly_price, '.'), '0', '0')) || 0;
	const newCredits = Number(String(data?.credits ?? '').replace(/[^0-9.]/g, ''));
	/* Resolve the active plan name from the cached plans query, falling back to trial status */
	const currentName = useMemo(() => {
		const list = queryClient.getQueryData(['plans'])?.plans || [];
		const match = _.find(list, (p) => p.monthly_price_id === getCurrentPlanID || p.trial_id === getCurrentPlanID);
		return authMe?.current_plan_name || match?.name || ((authMe?.trial_status || 'initial') !== 'initial' ? 'Free trial' : 'Current plan');
	}, [queryClient, getCurrentPlanID, authMe?.current_plan_name, authMe?.trial_status]);
	const billingNote = handleSubscribe?.data?.message
		? _.replace(handleSubscribe?.data?.message, handleSubscribe?.data?.next_billing_date, moment(handleSubscribe?.data?.next_billing_date).format('Do MMMM, YYYY'))
		: '';

	return (
		<>
			<Modal
				isOpen={confirmSubscription}
				onOpenChange={setConfirmSubscription}
				hideCloseButton
				backdrop="blur"
				size="md"
				classNames={{base: 'overflow-hidden rounded-[26px]', body: 'p-0', header: 'p-0', footer: 'p-0'}}
			>
				<ModalContent>
					{(onClose) => (
						<>
							{/* gradient hero banner — echoes the active plan card */}
							<ModalHeader>
								<div className="relative w-full overflow-hidden px-6 pb-7 pt-6 text-white bg-[radial-gradient(120%_120%_at_0%_0%,#3a0a8c_0%,#1b0740_55%,#11052b_100%)]">
									<span aria-hidden className="pointer-events-none absolute -top-16 -right-10 h-44 w-44 rounded-full bg-fuchsia-500/30 blur-3xl"/>
									<span aria-hidden className="pointer-events-none absolute -bottom-20 -left-12 h-44 w-44 rounded-full bg-primary/30 blur-3xl"/>
									<div className="relative flex items-center gap-4">
										<span className={`grid h-12 w-12 shrink-0 place-items-center rounded-2xl text-2xl ring-1 ring-white/25 ${isDowngrade ? 'bg-white/10' : 'bg-gradient-to-br from-fuchsia-500 to-primary'}`}>
											<i className={`hgi-stroke ${isDowngrade ? 'hgi-arrow-up-01 rotate-180' : 'hgi-rocket-01'}`}/>
										</span>
										<div>
											<p className="text-tiny font-semibold uppercase tracking-[0.18em] text-white/55">Plan change</p>
											<h2 className="font-sans text-xl font-bold tracking-tight">{isDowngrade ? 'Downgrade your plan' : 'Upgrade your plan'}</h2>
										</div>
									</div>
									<p className="relative mt-3 text-sm text-white/70">
										{isDowngrade
											? <>You&apos;re switching to a lighter plan. Your current perks stay active until the date below.</>
											: <>Unlock more credits and features instantly. Here&apos;s what&apos;s changing.</>}
									</p>
								</div>
							</ModalHeader>

							<ModalBody>
								<div className="px-6 pt-6">
									{/* current → new transition */}
									<div className="flex items-stretch gap-2.5">
										<div className="flex-1 rounded-2xl border border-slate-200 bg-slate-50 p-3.5">
											<p className="text-tiny font-semibold uppercase tracking-wide text-slate-400">Current</p>
											<p className="mt-1 font-sans text-base font-bold text-slate-800 truncate">{currentName}</p>
										</div>
										<span className="self-center grid h-8 w-8 shrink-0 place-items-center rounded-full bg-gradient-to-br from-primary to-secondary text-white shadow-[0_8px_20px_-8px_rgba(90,10,224,0.6)]">
											<i className="hgi-stroke hgi-arrow-right-01 text-sm"/>
										</span>
										<div className="flex-1 rounded-2xl p-3.5 ring-2 ring-primary/30 bg-gradient-to-br from-violet-50 to-fuchsia-50">
											<p className="text-tiny font-semibold uppercase tracking-wide text-primary/70">New plan</p>
											<p className="mt-1 font-sans text-base font-bold text-slate-900 truncate">{data?.name}</p>
											<p className="mt-0.5 font-sans text-sm font-semibold text-primary">
												${newPriceInt}<span className="text-slate-400 font-medium">{data?.name === 'Free' ? '' : ' /mo'}</span>
												{Number.isFinite(newCredits) && newCredits > 0 ? <span className="ml-2 text-tiny font-medium text-slate-500">· {newCredits.toLocaleString()} credits</span> : null}
											</p>
										</div>
									</div>

									{/* billing note */}
									{billingNote && (
										<div className="mt-4 flex items-start gap-3 rounded-2xl bg-violet-50/70 ring-1 ring-violet-100 p-3.5">
											<span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
												<i className="hgi-stroke hgi-calendar-03 text-sm"/>
											</span>
											<p className="text-sm leading-relaxed text-slate-600">{billingNote}</p>
										</div>
									)}
								</div>
							</ModalBody>

							<ModalFooter>
								<div className="flex w-full items-center gap-2 px-6 pb-6 pt-5">
									<Button radius="full" variant="light" className="text-slate-500 font-sans font-medium" onPress={onClose}>
										Cancel
									</Button>
									<Button
										fullWidth radius="full"
										isLoading={subscribePlan.isPending}
										endContent={!subscribePlan.isPending && <i className="hgi-stroke hgi-arrow-right-01 text-base"/>}
										className="bg-gradient-to-r from-primary to-secondary text-white font-sans font-semibold shadow-[0_16px_34px_-14px_rgba(90,10,224,0.7)]"
										onPress={() => {
											subscribePlan.mutateAsync({price_id: data?.monthly_price_id}).then(() => {
												onClose()
											})
										}}>
										{isDowngrade ? 'Confirm downgrade' : 'Confirm upgrade'}
									</Button>
								</div>
							</ModalFooter>
						</>
					)}
				</ModalContent>
			</Modal>
			<div
				key={(data?.monthly_price_id || planKey)}
				style={{'--glow': accent.glow}}
				className={`group relative isolate flex h-full flex-col overflow-hidden rounded-[26px] p-6 pl-7 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_30px_70px_-32px_var(--glow)] ${
					isCurrentPlan
						? 'text-white bg-[radial-gradient(120%_120%_at_0%_0%,#3a0a8c_0%,#1b0740_55%,#11052b_100%)] shadow-[0_30px_70px_-34px_rgba(90,10,224,0.65)]'
						: 'text-slate-900 bg-white ring-1 ring-slate-200/80 shadow-[0_20px_45px_-34px_rgba(17,12,46,0.35)] hover:ring-slate-300'
				}`}>
				{/* left identity rail */}
				<span aria-hidden className={`pointer-events-none absolute inset-y-0 left-0 w-[6px] bg-gradient-to-b ${accent.rail} transition-all duration-300 group-hover:w-2.5`}/>
				{/* oversized ghost tier numeral */}
				<span aria-hidden className={`pointer-events-none absolute -top-3 right-3 font-sans text-[5.5rem] font-bold leading-none tracking-tighter select-none ${isCurrentPlan ? 'text-white/[0.06]' : 'text-slate-900/[0.04]'}`}>{tierNo}</span>
				{isCurrentPlan && (
					<span aria-hidden className="pointer-events-none absolute -bottom-24 -right-16 h-56 w-56 rounded-full bg-fuchsia-500/25 blur-3xl"/>
				)}

				{/* status ribbon */}
				{isCurrentPlan ? (
					<div className="absolute right-4 top-4 z-10">
						<span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-tiny font-semibold text-white ring-1 ring-white/25 backdrop-blur-sm">
							<i className="hgi-stroke hgi-tick-02 text-xs"/> Current
						</span>
					</div>
				) : planKey === 1 ? (
					<div className="absolute right-4 top-4 z-10">
						<span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-2.5 py-1 text-tiny font-semibold text-white shadow-sm">
							<i className="hgi-stroke hgi-flash text-xs"/> Free for now
						</span>
					</div>
				) : null}

				{/* tier label */}
				<div className="relative flex items-center gap-2">
					<span className={`h-1.5 w-1.5 rounded-full ${isCurrentPlan ? 'bg-fuchsia-300' : accent.dot}`}/>
					<span className={`text-tiny font-semibold uppercase tracking-[0.18em] ${isCurrentPlan ? 'text-white/55' : 'text-slate-400'}`}>Tier {tierNo}</span>
				</div>
				<h3 className={`relative mt-1.5 font-sans text-2xl font-bold tracking-tight ${isCurrentPlan ? 'text-white' : 'text-slate-900'}`}>{data?.name}</h3>

				{/* editorial price */}
				<div className="relative mt-4 flex items-end gap-1">
					<span className={`mb-1.5 text-lg font-semibold ${isCurrentPlan ? 'text-white/70' : 'text-slate-400'}`}>$</span>
					<CountUp
						value={Number(_.get(_.split(data?.monthly_price, '.'), '0', '0')) || 0}
						className={`font-sans text-[2.75rem] font-bold leading-none tabular-nums ${isCurrentPlan ? 'text-white' : 'text-slate-900'}`}
					/>
					<span className={`mb-1.5 text-small font-medium ${isCurrentPlan ? 'text-white/55' : 'text-slate-400'}`}>{data?.name == 'Free' ? 'forever' : '/ month'}</span>
				</div>

				{/* perforated tear divider */}
				<div className="relative mt-6 flex items-center gap-2" aria-hidden>
					<span className={`h-2.5 w-2.5 -ml-[14px] rounded-full ${isCurrentPlan ? 'bg-[#1b0740]' : 'bg-white'} ${isCurrentPlan ? 'ring-1 ring-white/10' : 'ring-1 ring-slate-200'}`}/>
					<span className={`flex-1 border-t border-dashed ${isCurrentPlan ? 'border-white/20' : 'border-slate-200'}`}/>
					<span className={`h-2.5 w-2.5 -mr-[14px] rounded-full ${isCurrentPlan ? 'bg-[#11052b]' : 'bg-white'} ${isCurrentPlan ? 'ring-1 ring-white/10' : 'ring-1 ring-slate-200'}`}/>
				</div>

				{/* features */}
				<ul className="relative mt-5 flex flex-1 flex-col gap-3 text-sm">
					<li className="flex items-start gap-3">
						<span className={`mt-1 h-1.5 w-1.5 shrink-0 rotate-45 ${isCurrentPlan ? 'bg-fuchsia-300' : accent.dot}`}/>
						<span className={isCurrentPlan ? 'text-white/85' : 'text-slate-600'}><strong className={isCurrentPlan ? 'text-white' : 'text-slate-900'}>{data?.credits}</strong> credits {data?.name == 'Free' ? null : 'per month'}</span>
					</li>
					{data?.features?.map((feature, index) => (
						<li key={index} className="flex items-start gap-3">
							<span className={`mt-1 h-1.5 w-1.5 shrink-0 rotate-45 ${isCurrentPlan ? 'bg-fuchsia-300' : accent.dot}`}/>
							<span className={isCurrentPlan ? 'text-white/85' : 'text-slate-600'}>{feature}</span>
						</li>
					))}
				</ul>

				{/* CTA pinned to the bottom for aligned card heights */}
				<Button
					isLoading={isButtonLoading}
					onPress={onHandelSubscribe}
					fullWidth
					radius="full"
					isDisabled={isCurrentPlan}
					variant={isCurrentPlan ? 'bordered' : 'solid'}
					className={`!scale-100 mt-6 font-sans min-h-12 !opacity-100 uppercase tracking-wide ${
						isCurrentPlan
							? 'border-white/35 text-white'
							: 'bg-gradient-to-r from-primary to-secondary text-white shadow-[0_14px_30px_-12px_var(--glow)]'
					}`}
				>
					{getButtonName}
				</Button>
			</div>
		</>
	)
}
